import { CRM_BASE_API, CRM_PIPELINE_API } from 'modules/crm/api/crm-pipeline-api'
import { ReassignContactStageProps } from 'modules/crm/pipeline/type/pipeline-contact-interface'
import {
  PipelineInterface,
  PipelineUpdateInterface,
  StageContactInterface,
} from 'modules/crm/types/pipeline-interface'
import { MutableRefObject } from 'react'
import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import { DataWithPaginationInterface } from 'shared/types/pagination-interface'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { getStageRevalidationKey } from '../utils/get-stage-revalidation-key'
import { StageMutateType } from '../type/stage-mutate-type'

export const usePipelineActions = (
  pipelineId: number,
  stageMutatesRef?: MutableRefObject<Record<number, StageMutateType>>,
) => {
  const { revalidateModules } = useRevalidateModules()

  const { fetcher, isFetching } = useFetch<RequestMethodsEnum.put, PipelineInterface>({
    method: RequestMethodsEnum.put,
  })

  const { fetcher: postFetcher } = useFetch<RequestMethodsEnum.post>({
    method: RequestMethodsEnum.post,
  })

  const reassignContactStage = async ({ stageId, contactId }: ReassignContactStageProps) => {
    await postFetcher(`${CRM_BASE_API}/contacts/${contactId}/pipelines/${pipelineId}/stage`, {
      stageId,
    })
  }

  const addContactToStage = (
    data: DataWithPaginationInterface<StageContactInterface>[] | undefined,
    contact: StageContactInterface,
  ) =>
    data &&
    data.map((item, index) => ({
      ...item,
      items: index === 0 ? [contact, ...item.items] : item.items,
    }))

  const removeContactFromStage = (
    data: DataWithPaginationInterface<StageContactInterface>[] | undefined,
    contact: StageContactInterface,
  ) =>
    data &&
    data.map(item => ({
      ...item,
      items: item.items.filter(item => item.id !== contact.id),
    }))

  const updateDnDStages = async (
    currStageId: number,
    nextStageId: number,
    contact: StageContactInterface,
  ) => {
    const fromStage = stageMutatesRef?.current[currStageId]
    const toStage = stageMutatesRef?.current[nextStageId]

    if (fromStage && toStage) {
      try {
        await toStage(data => addContactToStage(data, contact), { revalidate: false })

        await fromStage(data => removeContactFromStage(data, contact), { revalidate: false })

        await reassignContactStage({
          stageId: Number(nextStageId),
          contactId: Number(contact.id),
        })

        await Promise.all([
          revalidateModules([getStageRevalidationKey(currStageId)]),
          revalidateModules([getStageRevalidationKey(nextStageId)]),
        ])
      } catch (e) {
        await fromStage(data => addContactToStage(data, contact), { revalidate: false })

        await toStage(data => removeContactFromStage(data, contact), { revalidate: false })
      }
    }
  }

  return {
    updatePipeline: (data: PipelineUpdateInterface) =>
      fetcher(`${CRM_PIPELINE_API}/${pipelineId}`, data),
    reassignContactStage,
    updateDnDStages,
    isFetching,
  }
}
